import { TouchEvent, useState } from 'react';
import { isMobileDevice } from '@optimus-web/common';
import { ActivableRenderer, ActivableRendererProps, Portal } from '@optimus-web/helpers';
import {
    DrawerBackdrop,
    DrawerContainer,
    DrawerContent,
    DrawerGesturePill,
    DrawerTopSection,
    DrawerWrapper,
    MIN_SWIPE_DISTANCE,
} from './style';

interface DrawerProps extends ActivableRendererProps {
    children?: React.ReactNode;
    height?: number;
    opened: boolean;
    onClose?: () => void;
    buttonLabel: string;
    portalContainerId?: string;
}

const DrawerActivatorFunction = ActivableRenderer<React.Component<DrawerProps>, DrawerProps>({
    activePropName: 'opened',
});

const Drawer = ({ children, height = 80, opened, onClose, buttonLabel, portalContainerId, ...other }: DrawerProps) => {
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientY);
    };

    const onTouchMove = (e: TouchEvent<HTMLDivElement>) => setTouchEnd(e.targetTouches[0].clientY);

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isSwipe = MIN_SWIPE_DISTANCE > distance;
        return isSwipe && onClose && onClose();
    };

    return (
        <Portal containerId={portalContainerId} {...other}>
            <DrawerWrapper>
                {opened && <DrawerBackdrop />}
                <DrawerContainer opened={opened} height={height} data-test-drawer>
                    <DrawerTopSection
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        {...(!isMobileDevice() && {
                            onClick: onClose,
                            tabIndex: 0,
                            onKeyDown: onClose,
                            role: 'button',
                            'aria-label': buttonLabel,
                        })}
                        data-test-drawer-top-section
                    >
                        <DrawerGesturePill />
                    </DrawerTopSection>
                    <DrawerContent data-test-drawer-content>{children}</DrawerContent>
                </DrawerContainer>
            </DrawerWrapper>
        </Portal>
    );
};

export default DrawerActivatorFunction(Drawer);
